import React, { useCallback, useEffect, useState } from "react";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { errMsg } from "../lib/api";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);
const weekAgoISO = () => new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10);
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const FEATURE_LABEL = { bronze: "Bronze", silver: "Silver Spotlight", gold: "Gold VIP" };

export function StatCard({ label, value, testid, accent = "text-white" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#131822] p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-slate-400">{label}</p>
      <p className={`mt-1 text-2xl font-black ${accent}`}>{value}</p>
    </div>
  );
}

function MoneyTable({ rows, first, firstKey, testid, onRowClick }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#131822]" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="text-left text-[10px] uppercase tracking-widest text-slate-400">
          <tr>{[first, "Count", "Captured", "Refunds", "Net", "Commission", "Pub net", "Held"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[firstKey]} onClick={onRowClick ? () => onRowClick(r) : undefined}
              className={`border-t border-white/5 ${onRowClick ? "cursor-pointer hover:bg-white/5" : ""}`}
              data-testid={`${testid}-row-${r[firstKey]}`}>
              <td className="px-4 py-2 font-semibold">{r._label || r[firstKey]}</td>
              <td className="px-4 py-2">{r.count}</td>
              <td className="px-4 py-2">{inr(r.gross)}</td>
              <td className="px-4 py-2 text-pink-300">{inr(r.refunds)}</td>
              <td className="px-4 py-2 font-bold">{inr(r.net)}</td>
              <td className="px-4 py-2 text-violet-300">{inr(r.commission)}</td>
              <td className="px-4 py-2 text-emerald-300">{inr(r.pub_net)}</td>
              <td className="px-4 py-2 text-amber-300">{inr(r.held)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">No payments in this range</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function RevenuePanel({ client, pubId, tick, onPubClick }) {
  const [from, setFrom] = useState(weekAgoISO);
  const [to, setTo] = useState(todayISO);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { date_from: from, date_to: to };
      if (pubId) params.pub_id = pubId;
      const { data } = await client.get("/revenue", { params });
      setData(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load revenue"));
    }
  }, [client, pubId, from, to]);

  useEffect(() => { load(); }, [load, tick]);

  const t = data?.totals || {};
  return (
    <div className="space-y-5" data-testid="revenue-panel">
      <p className="text-xs text-slate-500">Shout-out payments only — song bids are not counted.</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-400">From
          <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} data-testid="revenue-from"
            className="mt-1 h-10 border-white/10 bg-black/30 text-white" />
        </label>
        <label className="text-xs text-slate-400">To
          <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} data-testid="revenue-to"
            className="mt-1 h-10 border-white/10 bg-black/30 text-white" />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <StatCard label="Captured" value={inr(t.gross)} testid="rev-gross" />
        <StatCard label="Refunds" value={inr(t.refunds)} testid="rev-refunds" accent="text-pink-300" />
        <StatCard label="Net revenue" value={inr(t.net)} testid="rev-net" />
        <StatCard label="Commission" value={inr(t.commission)} testid="rev-commission" accent="text-violet-300" />
        <StatCard label="Pub net" value={inr(t.pub_net)} testid="rev-pub-net" accent="text-emerald-300" />
        <StatCard label="Held (awaiting DJ)" value={inr(t.held)} testid="rev-held" accent="text-amber-300" />
      </div>
      <div>
        <p className="mb-2 text-xs uppercase tracking-widest text-slate-400">Per shout-out package</p>
        <MoneyTable testid="rev-by-feature" first="Feature" firstKey="feature"
          rows={(data?.by_feature || []).map((r) => ({ ...r, _label: FEATURE_LABEL[r.feature] }))} />
      </div>
      {!pubId && (
        <div>
          <p className="mb-2 text-xs uppercase tracking-widest text-slate-400">Per pub</p>
          <MoneyTable testid="rev-by-pub" first="Pub" firstKey="pub_id" onRowClick={onPubClick}
            rows={(data?.by_pub || []).map((r) => ({ ...r, _label: r.pub_name }))} />
        </div>
      )}
      <div>
        <p className="mb-2 text-xs uppercase tracking-widest text-slate-400">Daily totals</p>
        <MoneyTable testid="rev-by-day" first="Date" firstKey="date" rows={[...(data?.by_day || [])].reverse()} />
      </div>
    </div>
  );
}

const KIND_STYLE = {
  request: "bg-violet-500/20 text-violet-200",
  payment: "bg-cyan-500/20 text-cyan-200",
  refund: "bg-pink-500/20 text-pink-200",
  approval: "bg-emerald-500/20 text-emerald-200",
  owner_message: "bg-amber-500/20 text-amber-200",
};

export function AlertsPanel({ client, pubId, tick, showPub }) {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/alerts", { params: pubId ? { pub_id: pubId } : {} });
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load alerts"));
    }
  }, [client, pubId]);

  useEffect(() => { load(); }, [load, tick]);

  return (
    <div className="space-y-2" data-testid="alerts-panel">
      {items.map((a) => (
        <div key={a.id} className="flex items-start gap-3 rounded-xl border border-white/10 bg-[#131822] p-3" data-testid={`alert-${a.id}`}>
          <Badge className={`shrink-0 border-0 text-[10px] uppercase ${KIND_STYLE[a.kind] || "bg-white/10"}`}>{(a.kind || "").replace("_", " ")}</Badge>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold">{a.title}</p>
            <p className="text-xs text-slate-400">{a.body}</p>
          </div>
          <div className="shrink-0 text-right text-[10px] text-slate-500">
            {showPub && <p className="font-semibold text-slate-300">{a.pub_name}</p>}
            {new Date(a.created_at).toLocaleString()}
          </div>
        </div>
      ))}
      {items.length === 0 && <p className="rounded-xl border border-dashed border-white/10 py-8 text-center text-sm text-slate-500">No alerts yet</p>}
    </div>
  );
}
